/**
 * iCal event expansion - pure functions, no store, no Dexie.
 *
 * Turns parsed events (series space, see types.ts) into per-day `EventOccurrence`s on the viewer's
 * calendar, for a window of viewer-local days (default: 30 days back to a year ahead).
 *
 * Recurrence runs on WALL-CLOCK time: the DTSTART wall clock is passed to rrule as pseudo-UTC, each
 * generated wall time is converted back through the series' resolved zone. A weekly Monday 00:30
 * Stockholm meeting therefore stays on Monday 00:30 Stockholm across DST, whatever the UTC date is.
 *
 * DST: a generated wall time that does not exist (spring-forward gap) is moved forward by the gap
 * (02:30 -> 03:30), the same rule RFC 5545 §3.3.5 gives for DTSTART. This deliberately departs
 * from §3.3.10 ("MUST be ignored"): Outlook, Google and Apple Calendar all show such an instance
 * moved, and a user comparing with the source calendar would otherwise miss a meeting. A wall time
 * that occurs twice (fall-back overlap) is its first occurrence.
 */

import dayjs from "dayjs"
import * as rruleModule from "rrule"
import type { EventOccurrence, ICalEventItem } from "./types"
import {
  DAY_MS,
  HOUR_MS,
  MINUTE_MS,
  localDayKey,
  localMidnight,
  localWallToInstant,
  instantToWall,
  wallDayKey,
  wallStartOfDay,
  wallToInstant,
} from "./zonedTime"

// rrule CJS/ESM interop: handle both Vite (named exports) and Node ESM (default.RRule)
const RRule: typeof rruleModule.RRule =
  (rruleModule as unknown as { default?: { RRule: typeof rruleModule.RRule } })
    .default?.RRule ?? rruleModule.RRule

// ---------------------------------------------------------------------------
// Window
// ---------------------------------------------------------------------------

/** Days before today and after today that are expanded by default. */
export const WINDOW_DAYS_BACK = 30
export const WINDOW_DAYS_AHEAD = 366

/** Most instances of one series kept in a window (a FREQ=MINUTELY feed must not exhaust memory). */
export const MAX_OCCURRENCES_PER_SERIES = 1000

/** Viewer-local days to expand, inclusive (`YYYY-MM-DD`). */
export interface ExpansionWindow {
  startDay: string
  endDay: string
  /** The viewer-local day "now" falls on; a capped series keeps the instances from here on. */
  today: string
}

/** The default window around `now`: 30 days back to a year ahead. */
export function expansionWindow(now: number = Date.now()): ExpansionWindow {
  const today = localDayKey(now)
  return {
    startDay: localDayKey(localMidnight(today, -WINDOW_DAYS_BACK)),
    endDay: localDayKey(localMidnight(today, WINDOW_DAYS_AHEAD)),
    today,
  }
}

export interface ExpandOptions {
  /** Defaults to `expansionWindow(now)`. */
  window?: ExpansionWindow
  /** "Now" for the default window. */
  now?: number
  maxPerSeries?: number
}

/** `YYYY-MM-DD` as pseudo-UTC midnight. */
function dayKeyToWall(day: string): number {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number]
  return Date.UTC(y, m - 1, d)
}

// ---------------------------------------------------------------------------
// Series space
// ---------------------------------------------------------------------------

type Space = { kind: "zoned"; zone: string } | { kind: "wall"; allDay: boolean }

function spaceOf(event: ICalEventItem): Space {
  if (event.allDay) return { kind: "wall", allDay: true }
  if (event.isFloating || !event.originalTzid) {
    // Legacy rows without a zone and not floating were UTC instants
    return event.isFloating ? { kind: "wall", allDay: false } : { kind: "zoned", zone: "UTC" }
  }
  return { kind: "zoned", zone: event.originalTzid }
}

/** The series' day (`YYYY-MM-DD`) of a series-space start - what DATE-valued exclusions name. */
function seriesDayKey(ms: number, space: Space): string {
  return wallDayKey(space.kind === "zoned" ? instantToWall(ms, space.zone) : ms)
}

// ---------------------------------------------------------------------------
// Recurrence (wall clock)
// ---------------------------------------------------------------------------

const SUB_DAILY_STEP_MS: Partial<Record<number, number>> = {
  [RRule.HOURLY]: HOUR_MS,
  [RRule.MINUTELY]: MINUTE_MS,
  [RRule.SECONDLY]: 1000,
}

/** Series-space starts (ms) of a recurring event within `[loWall, hiWall]` (wall clock). */
function recurrenceStarts(
  event: ICalEventItem,
  space: Space,
  loWall: number,
  hiWall: number,
  todayWall: number,
  max: number,
): number[] {
  const startMs = event.start * 1000
  const walls: number[] = []

  if (event.rrule) {
    const wallStart =
      space.kind === "zoned"
        ? event.wallStart !== undefined
          ? event.wallStart * 1000
          : instantToWall(startMs, space.zone)
        : startMs
    const options = RRule.parseString(event.rrule)
    let dtstart = wallStart
    // Sub-daily rules without COUNT: start near the window instead of iterating from a DTSTART
    // years back. Moving by whole intervals keeps every instance on the same grid.
    const unit = options.freq !== undefined ? SUB_DAILY_STEP_MS[options.freq] : undefined
    if (unit && !options.count) {
      const step = unit * (options.interval ?? 1)
      const behind = Math.floor((loWall - dtstart) / step) - 1
      if (behind > 0) dtstart += behind * step
    }
    const rule = new RRule({
      ...options,
      dtstart: new Date(dtstart),
      tzid: null,
      until: event.rruleUntil !== undefined ? new Date(event.rruleUntil * 1000) : null,
    })
    let hits = rule.between(new Date(loWall), new Date(hiWall), true, (_d, i) => i <= max)
    if (hits.length > max) {
      // Too dense to keep the whole window: keep the instances from today on
      hits = rule.between(new Date(Math.max(loWall, todayWall)), new Date(hiWall), true, (_d, i) => i < max)
    }
    for (const d of hits) walls.push(d.getTime())
  }

  const starts =
    space.kind === "zoned" ? walls.map((w) => wallToInstant(w, space.zone)) : walls
  if (event.rdates) {
    for (const r of event.rdates) {
      const ms = r * 1000
      const wall = space.kind === "zoned" ? instantToWall(ms, space.zone) : ms
      if (wall >= loWall && wall <= hiWall) starts.push(ms)
    }
  }
  starts.sort((a, b) => a - b)
  const unique = starts.filter((s, i) => i === 0 || s !== starts[i - 1])
  return unique.length > max ? unique.slice(0, max) : unique
}

// ---------------------------------------------------------------------------
// One instance -> per-day occurrences
// ---------------------------------------------------------------------------

function toOccurrences(
  event: ICalEventItem,
  space: Space,
  startMs: number,
  durationMs: number,
  window: ExpansionWindow,
): EventOccurrence[] {
  let days: string[]
  let occStart: number
  let occEnd: number

  if (space.kind === "wall" && space.allDay) {
    const first = wallStartOfDay(startMs)
    const endExclusive = Math.max(first + DAY_MS, wallStartOfDay(startMs + durationMs))
    days = []
    for (let d = first; d < endExclusive && days.length < 366; d += DAY_MS) days.push(wallDayKey(d))
    occStart = localMidnight(days[0]!)
    occEnd = localMidnight(days[days.length - 1]!, 1) - 1000
  } else {
    if (space.kind === "wall") {
      // Floating: the written wall clock on the viewer's own clock
      occStart = localWallToInstant(startMs)
      occEnd = localWallToInstant(startMs + durationMs)
    } else {
      occStart = startMs
      occEnd = startMs + durationMs
    }
    // An instance ending exactly at midnight does not touch the next day
    const lastInstant = occEnd > occStart ? occEnd - 1000 : occStart
    const firstDay = localDayKey(occStart)
    const lastDay = localDayKey(lastInstant)
    days = [firstDay]
    for (let i = 1; days[days.length - 1]! < lastDay && i < 366; i++) {
      days.push(localDayKey(localMidnight(firstDay, i)))
    }
  }

  const total = days.length
  const out: EventOccurrence[] = []
  for (let i = 0; i < total; i++) {
    const day = days[i]!
    if (day < window.startDay || day > window.endDay) continue
    out.push({
      ...event,
      occurrenceDate: day,
      occurrenceStart: Math.floor(occStart / 1000),
      occurrenceEnd: Math.floor(occEnd / 1000),
      multiDay: total > 1,
      isOccurring: total > 1 && i > 0 && i < total - 1,
    })
  }
  return out
}

function expandEvent(
  event: ICalEventItem,
  window: ExpansionWindow,
  max: number,
): EventOccurrence[] {
  const space = spaceOf(event)
  const durationMs = Math.max(0, (event.end - event.start) * 1000)
  const recurring = !!event.rrule || !!event.rdates?.length

  let starts: number[]
  if (recurring) {
    // The window as wall clock, padded so instances starting before it but reaching into it count
    const pad = 2 * DAY_MS + durationMs
    const loWall = dayKeyToWall(window.startDay) - pad
    const hiWall = dayKeyToWall(window.endDay) + 2 * DAY_MS
    starts = recurrenceStarts(event, space, loWall, hiWall, dayKeyToWall(window.today) - pad, max)
    if (!event.rrule) starts.unshift(event.start * 1000)
  } else {
    starts = [event.start * 1000]
  }

  const exStarts = event.exdates?.length ? new Set(event.exdates) : null
  const exDays = event.exdateDays?.length ? new Set(event.exdateDays) : null

  const out: EventOccurrence[] = []
  for (const s of starts) {
    if (exStarts?.has(Math.floor(s / 1000))) continue
    if (exDays?.has(seriesDayKey(s, space))) continue
    out.push(...toOccurrences(event, space, s, durationMs, window))
  }
  return out
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Expand parsed events into per-day occurrences inside the window. An event that cannot be
 * expanded is left out on its own; the rest of the calendar is unaffected.
 */
export function expandCalendarEvents(
  events: ICalEventItem[],
  calendarId: string,
  options: ExpandOptions = {},
): EventOccurrence[] {
  const run = createExpansion(calendarId, options)
  run.add(events)
  return run.finish()
}

/**
 * The same expansion in slices, yielding to the event loop between them, so a large calendar does
 * not block input (the store uses this; the result equals `expandCalendarEvents`).
 */
export async function expandCalendarEventsAsync(
  events: ICalEventItem[],
  calendarId: string,
  options: ExpandOptions = {},
  sliceSize = 250,
): Promise<EventOccurrence[]> {
  const run = createExpansion(calendarId, options)
  for (let i = 0; i < events.length; i += sliceSize) {
    if (i > 0) await new Promise<void>((resolve) => setTimeout(resolve, 0))
    run.add(events.slice(i, i + sliceSize))
  }
  return run.finish()
}

function createExpansion(calendarId: string, options: ExpandOptions) {
  const window = options.window ?? expansionWindow(options.now)
  const max = options.maxPerSeries ?? MAX_OCCURRENCES_PER_SERIES
  const seen = new Set<string>()
  const out: EventOccurrence[] = []
  let failed = 0
  return {
    add(events: ICalEventItem[]) {
      for (const event of events) {
        let occurrences: EventOccurrence[]
        try {
          occurrences = expandEvent({ ...event, calendarId }, window, max)
        } catch {
          failed++
          continue
        }
        for (const occ of occurrences) {
          // Exact duplicates only (the same VEVENT twice in a feed)
          const key = `${occ.uid}|${occ.occurrenceStart}|${occ.occurrenceDate}`
          if (seen.has(key)) continue
          seen.add(key)
          out.push(occ)
        }
      }
    },
    finish(): EventOccurrence[] {
      if (failed > 0) {
        console.warn(`[calendar] ${failed} event(s) could not be expanded and are not shown`)
      }
      return out
    },
  }
}

/** Format a Unix timestamp to local time. */
export function formatLocalTime(
  unixTime: number,
  format: string = "YYYY-MM-DD HH:mm",
): string {
  return dayjs.unix(unixTime).format(format)
}

/** Check if an event is currently active. */
export function isEventActive(event: EventOccurrence): boolean {
  const now = Math.floor(Date.now() / 1000)
  return now >= event.occurrenceStart && now <= event.occurrenceEnd
}

/** Filter occurrences for a specific date (YYYY-MM-DD). */
export function getEventsForDate(
  events: EventOccurrence[],
  date: string,
): EventOccurrence[] {
  return events.filter((event) => event.occurrenceDate === date)
}

/** Sort occurrences: all-day first, then by start time. */
export function sortEventsByTime(events: EventOccurrence[]): EventOccurrence[] {
  return [...events].sort((a, b) => {
    if (a.allDay && !b.allDay) return -1
    if (!a.allDay && b.allDay) return 1
    return a.occurrenceStart - b.occurrenceStart
  })
}

/**
 * Filter and deduplicate occurrences within a date range (inclusive).
 *
 * Ported from v1 UpcomingList.filterEventsForPeriod.
 * Uses lexicographic YYYY-MM-DD comparison (same as v1).
 */
export function filterEventsForPeriod(
  events: EventOccurrence[],
  periodStartDate: string,
  periodEndDate: string,
): EventOccurrence[] {
  if (!events || events.length === 0) return []

  // 1. Filter events whose occurrenceDate falls within the period
  const eventsInPeriod = events.filter((event) => {
    const d = event.occurrenceDate
    return d >= periodStartDate && d <= periodEndDate
  })

  // 2. Deduplicate: key by calendarId + uid + summary + allDay flag
  //    Keep earliest occurrence, count duplicates
  const unique = new Map<string, EventOccurrence>()
  for (const event of eventsInPeriod) {
    const key = `${event.calendarId ?? "cal"}_${event.uid}_${event.summary}_${event.allDay ? "allday" : ""}`
    const existing = unique.get(key)
    if (!existing) {
      unique.set(key, { ...event, occurrences: 1 })
    } else {
      existing.occurrences = (existing.occurrences ?? 1) + 1
      if (event.occurrenceStart < existing.occurrenceStart) {
        unique.set(key, { ...event, occurrences: existing.occurrences })
      }
    }
  }

  // 3. Sort: all-day first, then by start time
  return Array.from(unique.values()).sort((a, b) => {
    const allDayA = a.allDay ? 1 : 0
    const allDayB = b.allDay ? 1 : 0
    if (allDayA !== allDayB) return allDayB - allDayA
    return a.occurrenceStart - b.occurrenceStart
  })
}

/** webcal://, webcals:// and webcal+http(s):// subscription links as the URL to fetch. */
export function convertWebcalToHttps(url: string): string {
  const trimmed = url.trim()
  const m = /^(webcals?|webcal\+https?):\/\//i.exec(trimmed)
  if (!m) return trimmed
  const scheme = m[1]!.toLowerCase()
  const target = scheme === "webcal+http" ? "http://" : "https://"
  return target + trimmed.slice(m[0].length)
}
